import { apiClient } from '@/api/client';
import type {
  ReadyResult, MailStats, UsersStats, OrgStats,
  ActivityItem, NotificationItem,
} from '@/features/dashboard/types';

const rows = <T>(value: T[] | { data?: T[]; items?: T[] }): T[] =>
  Array.isArray(value) ? value : value.data ?? value.items ?? [];

/**
 * Dashboard reads. Each function calls an EXISTING gateway endpoint (owning
 * service noted). Nothing here is mocked; widgets fail soft individually if an
 * endpoint is unavailable.
 */
export const dashboardApi = {
  // mail-admin-service — platform/mail readiness
  ready: async (): Promise<ReadyResult> => (await apiClient.get<ReadyResult>('/health/ready')).data,

  // mail-admin-service — aggregated mail statistics
  statistics: async (windowHours = 24): Promise<MailStats> =>
    (await apiClient.get<MailStats>('/statistics', { params: { windowHours } })).data,

  // Queue depth moved to features/queue (single owner of /queue/summary).

  // organization service — derive real directory counts from the current tenant
  users: async (tenantId: string): Promise<UsersStats> => {
    const { data } = await apiClient.get<Array<{ status?: string }> | { data?: Array<{ status?: string }>; items?: Array<{ status?: string }> }>(
      `/tenants/${encodeURIComponent(tenantId)}/members`,
    );
    const members = rows(data);
    return {
      total: members.length,
      active: members.filter((member) => !member.status || member.status === 'active').length,
    };
  },

  // identity service — organizations available to this authenticated user
  organizations: async (): Promise<OrgStats> => {
    const { data } = await apiClient.get<Array<unknown> | { data?: unknown[]; items?: unknown[] }>('/auth/tenants');
    const organizations = rows(data);
    return { total: organizations.length, active: organizations.length };
  },

  // audit service — recent activity feed
  activity: async (tenantId: string, limit = 8): Promise<ActivityItem[]> => {
    type AuditRow = {
      id: string; actorLabel?: string; actorId?: string; action?: string;
      eventType?: string; resourceType?: string; resourceId?: string;
      occurredAt?: string; createdAt?: string; outcome?: string;
    };
    const { data } = await apiClient.get<{ items: AuditRow[] }>(`/tenants/${encodeURIComponent(tenantId)}/audit`, {
      params: { limit },
    });
    return data.items.map((item) => ({
      id: item.id,
      actor: item.actorLabel ?? item.actorId ?? 'System',
      action: item.action ?? item.eventType ?? 'Activity',
      target: [item.resourceType, item.resourceId].filter(Boolean).join(' · ') || undefined,
      timestamp: item.occurredAt ?? item.createdAt ?? new Date().toISOString(),
      severity: item.outcome === 'failure' || item.outcome === 'denied' ? 'warning' : 'info',
    }));
  },

  // notification service — user notifications
  notifications: async (tenantId: string, limit = 6): Promise<NotificationItem[]> => {
    type NotificationRow = {
      id: string; renderedSubject?: string | null; renderedBody?: string | null;
      status?: string; createdAt: string; readAt?: string | null;
    };
    const { data } = await apiClient.get<NotificationRow[]>(`/tenants/${encodeURIComponent(tenantId)}/in-app`);
    return data.slice(0, limit).map((item) => ({
      id: item.id,
      title: item.renderedSubject ?? 'Notification',
      message: item.renderedBody ?? undefined,
      level: item.status === 'failed' ? 'error' : 'info',
      createdAt: item.createdAt,
      read: Boolean(item.readAt),
    }));
  },
};
